import { JAVA_API_STATUS } from '../../../utils/axios-interceptor';
import { toast } from 'react-toastify';
import { CreateDirectorOutcome } from '../../../utils/constants/commonConstants';
import {
    fetchFundingHistoryDataService,
    getManagementProfileUserData,
    getManagementProfilesV2Data,
    getNetworkEntities,
    getLenderConnectionsData,
    updateManagementProfileCustomFieldsService,
    UpdateManagementProfileCustomFieldsPayload,
    createDirectorService,
    CreateDirectorPayload,
} from './service';
import { ManagementProfileWriteResult } from '../../../types/ownerHistory';

export async function fetchManagementProfilesV2(payload: { orgId: string; filters?: Record<string, any> | null }) {
    try {
        const response = await getManagementProfilesV2Data(payload);
        const responseData = response?.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                return response?.data?.responseBody;
            case JAVA_API_STATUS.ERROR:
                return toast.error(responseData?.responseMessage || 'Some error occurred');
        }
    } catch (err) {
        console.error('Error in fetching management profiles v2', err);
    }
}

export async function fetchManagementProfileUserData(payload: FetchManagementProfileDataPayload) {
    try {
        const response = await getManagementProfileUserData(payload);
        const responseData = response?.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                return response?.data?.responseBody;
            case JAVA_API_STATUS.ERROR:
                return toast.error(responseData?.responseMessage || 'Some error occurred');
        }
    } catch (err) {
        console.error('Error in fetching user data', err);
    }
}

export async function fetchFundingHistoryData(payload: { orgId: string; assesseeOrgId: string; taskStepsList: string[] }) {
    try {
        const response = await fetchFundingHistoryDataService(payload);
        const responseData = response?.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                return response?.data?.responseBody || {};
            case JAVA_API_STATUS.ERROR:
                return toast.error(responseData?.responseMessage || 'Some error occurred');
        }
    } catch (err) {
        console.error('Error in fetching user data', err);
    }
}

export async function fetchCompaniesNetworkData(payload: { orgId: string }) {
    try {
        const response = await getNetworkEntities({ assesseeOrgId: payload.orgId });
        const responseData = response?.data?.responseData;

        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS: {
                const body = response?.data?.responseBody;
                return {
                    vendorFor: body?.vendorFor || [],
                    customerOf: body?.customerOf || [],
                    lastUpdatedAt: body?.lastUpdatedAt,
                    totalCompaniesInNetwork: body?.totalCompaniesInNetwork ?? 0,
                    networkRelatedPartyEntities: body?.networkRelatedPartyEntities || {},
                };
            }
            case JAVA_API_STATUS.ERROR:
            default:
                toast.error(responseData?.responseMessage || 'Some error occurred');
                return null;
        }
    } catch (err) {
        console.error('Error in fetching companies network data', err);
        return null;
    }
}

export async function fetchLenderConnectionsData(payload: { orgId: string }) {
    try {
        const response = await getLenderConnectionsData(payload);
        const responseData = response?.data?.responseData;

        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                return response?.data?.data || [];
            case JAVA_API_STATUS.ERROR:
            default:
                return [];
        }
    } catch (err) {
        console.error('Error in fetching lender connections data', err);
        return [];
    }
}

/**
 * Persists an edit to one or more Management Profile custom fields (write path, D2).
 * Returns whether the call succeeded; throws (after logging) on a request failure so the
 * caller's own rollback (useManagementProfileTabLogic's .catch) still fires — write paths
 * must not swallow errors.
 *
 * polus BEFE: the unifier cannot express a failed write as an HTTP status or
 * via its own `responseData.responseCode` — that stays 20 even when aica rejected the write
 * or was unreachable. `transformManagementProfileWriteResult` (polus
 * DataTransformationScript.groovy) emits the real verdict as `managementProfileWrite.status`
 * instead ('SAVED' | 'FAILED', with `reason: 'REJECTED' | 'UPSTREAM_UNAVAILABLE'` on
 * failure). Reading the outer responseData here would make every rejection look like a save
 * — fail-closed (`false`) on anything other than an explicit 'SAVED'.
 */
export async function updateManagementProfileCustomFields(payload: UpdateManagementProfileCustomFieldsPayload): Promise<boolean> {
    try {
        const response = await updateManagementProfileCustomFieldsService(payload);
        const writeResult: ManagementProfileWriteResult | undefined = response?.data?.responseBody?.managementProfileWrite;
        const isSaved = writeResult?.status === 'SAVED';
        if (!isSaved) {
            // Both failure reasons ('REJECTED' — a real aica validation message; 'UPSTREAM_UNAVAILABLE'
            // — a generic retry message) already carry the right user-facing text from polus.
            toast.error(writeResult?.message || 'Could not save. Please retry.');
        }
        return isSaved;
    } catch (err: any) {
        // Deliberately not the error object: an Axios error carries `config.data`, which on this
        // endpoint is the director's phone, email and address. Only the status is logged.
        console.error('Error updating management profile custom fields', { status: err?.response?.status });
        throw err;
    }
}

type FetchManagementProfileDataPayload = {
    orgId: string;
    entityId: string;
    name: string;
    taskStepsList: string[];
};

export interface CreateDirectorResult {
    ok: boolean;
    message?: string;
    errorCode?: string;
}

/**
 * Creates a director via the already-shipped polus BEFE `POST /unifier/createDirector` route.
 * Branches on `responseBody.outcome` — NOT `responseData`, which always reports
 * `{responseCode:20}` even for a duplicate or a rejected write (same fail-closed pattern as
 * updateManagementProfileCustomFields above). A request failure (network/5xx) is NOT swallowed
 * here — it propagates to the caller (the modal), which surfaces the generic error toast; never
 * log PAN/DIN/phone/email.
 */
export async function createDirector(payload: CreateDirectorPayload, organizationId: string): Promise<CreateDirectorResult> {
    const response = await createDirectorService(payload, organizationId);
    const responseBody = response?.data?.responseBody;
    if (responseBody?.outcome === CreateDirectorOutcome.CREATED) return { ok: true };
    if (responseBody?.outcome === CreateDirectorOutcome.DUPLICATE) {
        return { ok: false, message: responseBody?.message, errorCode: responseBody?.errorCode };
    }
    return { ok: false };
}
